import { extendTheme, type ThemeConfig } from '@chakra-ui/react';

// Theme configuration
const config: ThemeConfig = {
  initialColorMode: 'light',
  useSystemColorMode: true,
};

// Jedno písmo pro celý web – stejné, jako je v textu na úvodní stránce.
// Zdroj pravdy je proměnná --font-main v index.css.
const FONT_MAIN = `'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif`;

const fonts = {
  heading: FONT_MAIN,
  body: FONT_MAIN,
  mono: `source-code-pro, Menlo, Monaco, Consolas, 'Courier New', monospace`,
};

// Custom colors for light and dark mode
const colors = {
  brand: {
    900: '#000000',
    800: '#222222',
    700: '#333333',
    100: '#f8f8f8',
    50: '#ffffff',
  },
};

// Component specific overrides
const components = {
  // Nadpisy jemně – Chakra má ve výchozím stavu bold, což je na tenhle web moc těžké
  Heading: {
    baseStyle: {
      fontFamily: FONT_MAIN,
      fontWeight: 300,
    },
  },
  Text: {
    baseStyle: {
      fontFamily: FONT_MAIN,
    },
  },
  // Popisky polí (kontakt i rezervace) – stejné hodnoty jako .form-label
  // v Contact.css. Chakra má výchozí váhu 500 a 1rem a její styly se vkládají
  // až za CSS soubory, takže by třídu přebila a popisky by byly tučné.
  FormLabel: {
    baseStyle: {
      fontFamily: FONT_MAIN,
      fontSize: '0.8rem',
      fontWeight: 400,
      letterSpacing: '1px',
      textTransform: 'uppercase',
      mb: '0.4rem',
    },
  },
  // Hvězdička povinného pole – tenká, v barvě popisku, ne křiklavě červená
  Form: {
    baseStyle: {
      requiredIndicator: {
        color: 'currentColor',
        fontWeight: 300,
        opacity: 0.5,
      },
    },
  },
  Button: {
    baseStyle: (props: any) => ({
      fontFamily: FONT_MAIN,
      fontWeight: 400,
      _hover: {
        bg: props.colorMode === 'dark' ? 'gray.600' : 'gray.200',
      }
    }),
    variants: {
      solid: (props: any) => ({
        bg: props.colorMode === 'dark' ? 'gray.700' : 'black',
        color: props.colorMode === 'dark' ? 'white' : 'white',
        _hover: {
          bg: props.colorMode === 'dark' ? 'gray.600' : 'gray.700',
        }
      }),
    }
  },
  Input: {
    variants: {
      outline: (props: any) => ({
        field: {
          bg: props.colorMode === 'dark' ? 'gray.700' : 'white',
          borderColor: props.colorMode === 'dark' ? 'gray.600' : 'rgba(0,0,0,0.15)',
          borderRadius: '2px',
          _hover: {
            borderColor: props.colorMode === 'dark' ? 'gray.400' : 'rgba(0,0,0,0.35)',
          },
          _focus: {
            borderColor: props.colorMode === 'dark' ? 'white' : 'black',
            boxShadow: props.colorMode === 'dark' ? '0 0 0 1px white' : '0 0 0 1px black',
          }
        }
      }),
    }
  },
  Select: {
    variants: {
      outline: (props: any) => ({
        field: {
          bg: props.colorMode === 'dark' ? 'gray.700' : 'white',
          borderColor: props.colorMode === 'dark' ? 'gray.600' : 'rgba(0,0,0,0.15)',
          borderRadius: '2px',
          _hover: {
            borderColor: props.colorMode === 'dark' ? 'gray.400' : 'rgba(0,0,0,0.35)',
          },
          _focus: {
            borderColor: props.colorMode === 'dark' ? 'white' : 'black',
            boxShadow: props.colorMode === 'dark' ? '0 0 0 1px white' : '0 0 0 1px black',
          }
        }
      }),
    }
  },
  Textarea: {
    variants: {
      outline: (props: any) => ({
        bg: props.colorMode === 'dark' ? 'gray.700' : 'white',
        borderColor: props.colorMode === 'dark' ? 'gray.600' : 'rgba(0,0,0,0.15)',
        borderRadius: '2px',
        _hover: {
          borderColor: props.colorMode === 'dark' ? 'gray.400' : 'rgba(0,0,0,0.35)',
        },
        _focus: {
          borderColor: props.colorMode === 'dark' ? 'white' : 'black',
          boxShadow: props.colorMode === 'dark' ? '0 0 0 1px white' : '0 0 0 1px black',
        }
      }),
    }
  }
};

// Extend the theme
const theme = extendTheme({
  config,
  colors,
  fonts,
  components,
  styles: {
    global: (props: any) => ({
      body: {
        bg: props.colorMode === 'dark' ? 'gray.900' : 'white',
        color: props.colorMode === 'dark' ? 'gray.100' : 'gray.800',
      },
      // Apply consistent background colors for sections
      '.contact-container, .about-container, .portfolio-container': {
        bg: props.colorMode === 'dark' ? 'gray.800' : '#f8f8f8',
      },
    }),
  },
});

export default theme;
